import React, { useState, useEffect, useCallback } from 'react';
import { useDashboard } from './DashboardContext';
import { analyticsApi } from '../../services/api';
import AdminPanelPage from './AdminPanelPage';
import StreamManagerPage from './StreamManagerPage';
import TelegramSettings from '../../components/TelegramSettings';
import CoachingPortal from './CoachingPortal';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import {
  Users,
  AlertTriangle,
  UserPlus,
  Clock,
  RefreshCw,
  Activity,
  BarChart3,
  BookOpen,
  MessageSquare,
  Ticket,
  Crown,
  ExternalLink,
  Radio,
  X,
  Calendar,
  CheckCircle,
  Send,
  Copy,
  Check,
  CreditCard
} from 'lucide-react';

const AdminDashboard = () => {
  const { 
    setShowMembershipModal,
    setShowChannelsModal,
    setShowUsersModal,
    setShowCoursesModal,
    handleOpenUserManagement,
    loadMembershipActivity,
    failedPaymentsCount,
    newMembersCount,
    streamData
  } = useDashboard();
  
  const [chartData, setChartData] = useState([]);
  const [alerts, setAlerts] = useState({ failed_payments: 0, new_members_pending: 0, expiring_soon: 0 });
  const [expiringMembers, setExpiringMembers] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);
  const [refreshing, setRefreshing] = useState(false);
  
  // Modal states for coupons, membership activity, and stream manager (managed locally)
  const [showCouponsModal, setShowCouponsModal] = useState(false);
  const [showMembershipActivityModal, setShowMembershipActivityModal] = useState(false);
  const [showStreamManagerModal, setShowStreamManagerModal] = useState(false);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [copiedMentorshipLink, setCopiedMentorshipLink] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(null);
  const [mentorshipPaymentType, setMentorshipPaymentType] = useState('full'); // 'full' or 'installment'
  const [generatingCheckout, setGeneratingCheckout] = useState(null);
  
  // Copy to clipboard with fallback for restricted environments
  const copyToClipboard = async (text, linkId) => {
    let success = false;
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        success = true;
      }
    } catch (err) {
      console.log('Clipboard API failed, trying fallback');
    }
    
    if (!success) {
      try {
        // Fallback for restricted environments
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const result = document.execCommand('copy');
        document.body.removeChild(textArea);
        success = result;
      } catch (err) {
        console.log('execCommand fallback failed');
      }
    }
    
    if (success) {
      setCopiedMentorshipLink(linkId);
      setTimeout(() => setCopiedMentorshipLink(null), 2000);
    } else {
      // Show modal with the link for manual copying
      setShowLinkModal({ linkId, url: text });
    }
  };
  
  // Close link modal
  const closeLinkModal = () => setShowLinkModal(null);
  
  // Persist Coaching Portal state across refreshes
  const [showCoachingPortal, setShowCoachingPortalState] = useState(() => {
    return localStorage.getItem('hyperDriveShowCoachingPortal') === 'true';
  });
  
  const setShowCoachingPortal = (show) => {
    setShowCoachingPortalState(show);
    localStorage.setItem('hyperDriveShowCoachingPortal', show ? 'true' : 'false');
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled([
        analyticsApi.getRevenueChart(timeRange),
        analyticsApi.getAlerts(),
        analyticsApi.getExpiringMemberships(),
        analyticsApi.getRecentActivity(15)
      ]);

      // Extract data from settled promises, using null for failed ones
      const [chartResult, alertsResult, expiringResult, activityResult] = results;

      if (chartResult.status === 'fulfilled') {
        setChartData(chartResult.value.data || []);
      } else {
        console.error('Failed to fetch revenue chart:', chartResult.reason);
        setChartData([]);
      }

      if (alertsResult.status === 'fulfilled') {
        setAlerts(alertsResult.value);
      }

      if (expiringResult.status === 'fulfilled') {
        setExpiringMembers(expiringResult.value.expiring || []);
      }

      if (activityResult.status === 'fulfilled') {
        setRecentActivity(activityResult.value.activities || []);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchDashboardData();
    // Also load membership activity counts on mount
    loadMembershipActivity();
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
    loadMembershipActivity();
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'signup': return <UserPlus size={16} className="text-blue-400" />;
      case 'failed_payment': return <AlertTriangle size={16} className="text-red-400" />;
      default: return <Activity size={16} className="text-zinc-400" />;
    }
  };

  // Emit events to open modals in AdminPanelPage
  const emitOpenModal = (eventName) => {
    window.dispatchEvent(new CustomEvent(eventName));
  };

  if (loading) {
    return (
      <div className="admin-dashboard-loading" data-testid="admin-dashboard-loading">
        <RefreshCw size={32} className="animate-spin text-blue-500" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  const quickActions = [
    // Row 1 - 4 items
    {
      id: 'manage-users',
      label: 'User Management',
      icon: Users,
      onClick: () => { handleOpenUserManagement(); setShowUsersModal(true); },
      color: 'blue'
    },
    {
      id: 'manage-courses',
      label: 'Course Management',
      icon: BookOpen,
      onClick: () => setShowCoursesModal(true),
      color: 'green'
    },
    {
      id: 'membership-plans',
      label: 'Membership Plans',
      icon: Crown,
      onClick: () => setShowMembershipModal(true),
      color: 'purple'
    },
    {
      id: 'community-channels',
      label: 'Community Channels',
      icon: MessageSquare,
      onClick: () => setShowChannelsModal(true),
      color: 'cyan'
    },
    // Row 2 - 4 items (staggered)
    {
      id: 'coupon-codes',
      label: 'Coupon Codes',
      icon: Ticket,
      onClick: () => emitOpenModal('openCouponsModal'),
      color: 'amber'
    },
    {
      id: 'membership-activity',
      label: 'Membership Activity',
      icon: Activity,
      onClick: () => {
        loadMembershipActivity();
        emitOpenModal('openMembershipActivityModal');
      },
      badge: (failedPaymentsCount + newMembersCount) > 0 ? (failedPaymentsCount + newMembersCount) : null,
      color: 'red'
    },
    {
      id: 'stream-manager',
      label: streamData?.isLive ? 'LIVE NOW' : 'Stream Manager',
      icon: Radio,
      onClick: () => setShowStreamManagerModal(true),
      color: streamData?.isLive ? 'live' : 'rose',
      isLive: streamData?.isLive
    },
    {
      id: 'telegram-settings',
      label: 'Telegram Bot',
      icon: Send,
      onClick: () => setShowTelegramModal(true),
      color: 'sky'
    },
    {
      id: 'coaching-portal',
      label: 'Coaching Portal',
      icon: Crown,
      onClick: () => setShowCoachingPortal(true),
      color: 'yellow'
    },
    {
      id: 'stripe-dashboard',
      label: 'Stripe Dashboard',
      icon: ExternalLink,
      onClick: () => window.open('https://dashboard.stripe.com', '_blank'),
      color: 'indigo'
    }
  ];

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      {/* Header */}
      <div className="admin-dashboard-header">
        <div className="header-left">
          <h1 className="dashboard-title">Admin Dashboard</h1>
          <p className="dashboard-subtitle">Manage your trading academy</p>
        </div>
        <div className="header-right">
          <div className="time-range-selector">
            {[7, 30, 90].map((days) => (
              <button
                key={days}
                className={`time-range-btn ${timeRange === days ? 'active' : ''}`}
                onClick={() => setTimeRange(days)}
                data-testid={`time-range-${days}`}
              >
                {days}d
              </button>
            ))}
          </div>
          <button 
            className="refresh-btn" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-dashboard"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Alerts Bar */}
      {(failedPaymentsCount > 0 || newMembersCount > 0 || alerts.expiring_soon > 0) && (
        <div className="alerts-bar" data-testid="alerts-bar">
          {failedPaymentsCount > 0 && (
            <button 
              className="alert-chip danger"
              onClick={() => {
                loadMembershipActivity();
                emitOpenModal('openMembershipActivityModal');
              }}
              data-testid="alert-failed-payments"
            >
              <AlertTriangle size={14} />
              <span>{failedPaymentsCount} failed payment{failedPaymentsCount !== 1 ? 's' : ''}</span>
            </button>
          )}
          {newMembersCount > 0 && (
            <button 
              className="alert-chip success"
              onClick={() => {
                loadMembershipActivity();
                emitOpenModal('openMembershipActivityModal');
              }}
              data-testid="alert-new-members"
            >
              <UserPlus size={14} />
              <span>{newMembersCount} need TradingView access</span>
            </button>
          )}
          {alerts.expiring_soon > 0 && (
            <button 
              className="alert-chip warning"
              data-testid="alert-expiring"
            >
              <Clock size={14} />
              <span>{alerts.expiring_soon} expiring this week</span>
            </button>
          )}
        </div>
      )}

      {/* Quick Actions Cards - Primary admin controls */}
      <div className="quick-actions-cards-grid" data-testid="quick-actions-grid">
        {quickActions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.id}
              className={`quick-action-card ${action.color} ${action.isLive ? 'live-pulse' : ''}`}
              onClick={action.onClick}
              data-testid={`action-${action.id}`}
            >
              <div className={`quick-action-card-icon ${action.color}`}>
                <Icon size={24} />
              </div>
              <span className="quick-action-card-label">{action.label}</span>
              {action.badge && (
                <span className="quick-action-card-badge">{action.badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Mentorship Links Section */}
      <div className="mentorship-links-section" data-testid="mentorship-links-section">
        <div className="mentorship-links-header">
          <CreditCard size={20} />
          <h3>Mentorship Links</h3>
          <span className="mentorship-links-subtitle">Generate checkout links for clients</span>
          <div className="payment-type-toggle">
            <button 
              className={`payment-type-btn ${mentorshipPaymentType === 'full' ? 'active' : ''}`}
              onClick={() => setMentorshipPaymentType('full')}
            >
              Full Payment
            </button>
            <button 
              className={`payment-type-btn ${mentorshipPaymentType === 'installment' ? 'active' : ''}`}
              onClick={() => setMentorshipPaymentType('installment')}
            >
              2 Payments
            </button>
          </div>
        </div>
        <div className="mentorship-links-row">
          <div className="mentorship-link-card">
            <div className="mentorship-link-details">
              <span className="mentorship-link-title">6 Month Mini Mentorship (Bi Weekly)</span>
              <span className="mentorship-link-price">
                {mentorshipPaymentType === 'full' ? '$1,500' : '$750 × 2 (2nd payment at month 3)'}
              </span>
            </div>
            <div className="mentorship-link-actions">
              <button 
                className={`mentorship-copy-btn ${copiedMentorshipLink === 'bi-weekly' ? 'copied' : ''}`}
                onClick={() => {
                  const baseUrl = `${window.location.origin}/mentorship/bi-weekly`;
                  const url = mentorshipPaymentType === 'installment' ? `${baseUrl}?payment=2` : baseUrl;
                  copyToClipboard(url, 'bi-weekly');
                }}
                data-testid="copy-biweekly-link"
                title="Copy page link"
              >
                {copiedMentorshipLink === 'bi-weekly' ? <Check size={16} /> : <Copy size={16} />}
                Page Link
              </button>
              <button 
                className={`mentorship-checkout-btn ${generatingCheckout === 'bi-weekly' ? 'loading' : ''} ${copiedMentorshipLink === 'bi-weekly-checkout' ? 'copied' : ''}`}
                onClick={async () => {
                  setGeneratingCheckout('bi-weekly');
                  try {
                    const API = process.env.REACT_APP_BACKEND_URL;
                    const response = await fetch(`${API}/api/mentorship/checkout/bi-weekly`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ payment_type: mentorshipPaymentType })
                    });
                    const data = await response.json();
                    if (data.checkout_url) {
                      copyToClipboard(data.checkout_url, 'bi-weekly-checkout');
                    }
                  } catch (err) {
                    console.error('Failed to generate checkout:', err);
                  }
                  setGeneratingCheckout(null);
                }}
                data-testid="checkout-biweekly-link"
                title="Generate Stripe checkout link"
              >
                {generatingCheckout === 'bi-weekly' ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : copiedMentorshipLink === 'bi-weekly-checkout' ? (
                  <Check size={16} />
                ) : (
                  <ExternalLink size={16} />
                )}
                {copiedMentorshipLink === 'bi-weekly-checkout' ? 'Copied!' : 'Checkout Link'}
              </button>
            </div>
          </div>
          <div className="mentorship-link-card">
            <div className="mentorship-link-details">
              <span className="mentorship-link-title">6 Month Mentorship (Weekly)</span>
              <span className="mentorship-link-price">
                {mentorshipPaymentType === 'full' ? '$5,000' : '$2,500 × 2 (2nd payment at month 3)'}
              </span>
            </div>
            <div className="mentorship-link-actions">
              <button 
                className={`mentorship-copy-btn ${copiedMentorshipLink === 'weekly' ? 'copied' : ''}`}
                onClick={() => {
                  const baseUrl = `${window.location.origin}/mentorship/weekly`;
                  const url = mentorshipPaymentType === 'installment' ? `${baseUrl}?payment=2` : baseUrl;
                  copyToClipboard(url, 'weekly');
                }}
                data-testid="copy-weekly-link"
                title="Copy page link"
              >
                {copiedMentorshipLink === 'weekly' ? <Check size={16} /> : <Copy size={16} />}
                Page Link
              </button>
              <button 
                className={`mentorship-checkout-btn ${generatingCheckout === 'weekly' ? 'loading' : ''} ${copiedMentorshipLink === 'weekly-checkout' ? 'copied' : ''}`}
                onClick={async () => {
                  setGeneratingCheckout('weekly');
                  try {
                    const API = process.env.REACT_APP_BACKEND_URL;
                    const response = await fetch(`${API}/api/mentorship/checkout/weekly`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ payment_type: mentorshipPaymentType })
                    });
                    const data = await response.json();
                    if (data.checkout_url) {
                      copyToClipboard(data.checkout_url, 'weekly-checkout');
                    }
                  } catch (err) {
                    console.error('Failed to generate checkout:', err);
                  }
                  setGeneratingCheckout(null);
                }}
                data-testid="checkout-weekly-link"
                title="Generate Stripe checkout link"
              >
                {generatingCheckout === 'weekly' ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : copiedMentorshipLink === 'weekly-checkout' ? (
                  <Check size={16} />
                ) : (
                  <ExternalLink size={16} />
                )}
                {copiedMentorshipLink === 'weekly-checkout' ? 'Copied!' : 'Checkout Link'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-content-grid">
        {/* Revenue Chart */}
        <div className="dashboard-card chart-card" data-testid="revenue-chart-card">
          <div className="card-header">
            <div className="card-title-row">
              <BarChart3 size={20} className="text-blue-500" />
              <h3>Revenue Overview</h3>
            </div>
            <span className="card-period">Last {timeRange} days</span>
          </div>
          <div className="chart-container">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    stroke="#71717A"
                    tick={{ fill: '#71717A', fontSize: 11 }}
                    axisLine={{ stroke: '#27272A' }}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="#71717A"
                    tick={{ fill: '#71717A', fontSize: 11 }}
                    axisLine={{ stroke: '#27272A' }}
                    tickLine={false}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181B',
                      border: '1px solid #27272A',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                    labelFormatter={formatDate}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    fill="url(#revenueGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="chart-empty">
                <BarChart3 size={48} className="text-zinc-600" />
                <p>No revenue data for this period</p>
              </div>
            )}
          </div>
        </div>

        {/* Expiring Memberships */}
        <div className="dashboard-card expiring-card" data-testid="expiring-memberships-card">
          <div className="card-header">
            <div className="card-title-row">
              <Clock size={20} className="text-amber-500" />
              <h3>Expiring This Week</h3>
            </div>
            <span className="card-count">{expiringMembers.length}</span>
          </div>
          <div className="expiring-list">
            {expiringMembers.length > 0 ? (
              expiringMembers.slice(0, 5).map((member, idx) => (
                <div key={idx} className="expiring-item">
                  <div className="expiring-user">
                    <span className="user-name">{member.user_name}</span>
                    <span className="user-email">{member.user_email}</span>
                  </div>
                  <div className="expiring-details">
                    <span className="membership-name">{member.membership_name}</span>
                    <span className="expires-date">
                      <Calendar size={12} />
                      {formatDate(member.expires_at)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <CheckCircle size={32} className="text-green-500" />
                <p>No memberships expiring soon</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="dashboard-card activity-card" data-testid="recent-activity-card">
          <div className="card-header">
            <div className="card-title-row">
              <Activity size={20} className="text-violet-500" />
              <h3>Recent Activity</h3>
            </div>
          </div>
          <div className="activity-list">
            {recentActivity.length > 0 ? (
              recentActivity.slice(0, 8).map((activity, idx) => (
                <div key={idx} className="activity-item">
                  <div className="activity-icon">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="activity-content">
                    <p className="activity-description">{activity.description}</p>
                    <span className="activity-time">{formatDateTime(activity.timestamp)}</span>
                  </div>
                  {activity.amount > 0 && (
                    <span className={`activity-amount ${activity.type === 'failed_payment' ? 'failed' : ''}`}>
                      {formatCurrency(activity.amount)}
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div className="empty-state">
                <Activity size={32} className="text-zinc-600" />
                <p>No recent activity</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Link Copy Modal - fallback when clipboard API is blocked */}
      {showLinkModal && (
        <div className="modal-overlay" onClick={closeLinkModal}>
          <div className="modal-content link-copy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <Copy size={20} style={{ marginRight: '0.5rem' }} />
                Copy Link
              </h2>
              <button className="modal-close" onClick={closeLinkModal}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>
                Select and copy the link below:
              </p>
              <input
                type="text"
                value={showLinkModal.url}
                readOnly
                onClick={(e) => e.target.select()}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  borderRadius: '8px',
                  color: '#e2e8f0',
                  fontSize: '0.875rem',
                  fontFamily: 'monospace'
                }}
              />
              <p style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.75rem' }}>
                Press Ctrl+C (Cmd+C on Mac) to copy after selecting
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Render AdminPanelPage modals only */}
      <AdminPanelPage modalsOnly={true} />

      {/* Stream Manager Modal */}
      {showStreamManagerModal && (
        <div className="modal-overlay" onClick={() => setShowStreamManagerModal(false)}>
          <div className="modal-content stream-manager-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <Radio size={20} style={{ marginRight: '0.5rem' }} />
                Stream Manager
              </h2>
              <button className="modal-close" onClick={() => setShowStreamManagerModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body stream-manager-body">
              <StreamManagerPage />
            </div>
          </div>
        </div>
      )}

      {/* Telegram Settings Modal */}
      {showTelegramModal && (
        <div className="modal-overlay" onClick={() => setShowTelegramModal(false)}>
          <div className="modal-content telegram-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <Send size={20} style={{ marginRight: '0.5rem' }} />
                Telegram Bot Settings
              </h2>
              <button className="modal-close" onClick={() => setShowTelegramModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <TelegramSettings />
            </div>
          </div>
        </div>
      )}

      {/* Coaching Portal */}
      {showCoachingPortal && (
        <CoachingPortal onClose={() => setShowCoachingPortal(false)} />
      )}
    </div>
  );
};

export default AdminDashboard;
